import { useNavigate } from 'react-router-dom'
import { lumi } from '../lib/lumi'
import {ArrowLeft, Loader, Calendar, AlertTriangle, Clock} from 'lucide-react'
import { useState, useEffect } from 'react'

export default function CalendarioEntregas() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [relatorio, setRelatorio] = useState('')
  const [contratos, setContratos] = useState<any[]>([])
  const [propostas, setPropostas] = useState<any[]>([])

  useEffect(() => {
    carregarDados()
  }, [])

  const carregarDados = async () => {
    try {
      const user = lumi.auth.user
      const userId = user?.userId || user?.id || user?._id

      if (!userId) return

      const [resultContratos, resultPropostas] = await Promise.all([
        lumi.entities.contratos.list({
          filter: { userId: userId },
          sort: { createdAt: -1 },
          limit: 1000
        }),
        lumi.entities.propostas.list({
          filter: { creator: userId, status: 'aceita' },
          sort: { createdAt: -1 },
          limit: 1000
        })
      ])

      setContratos(resultContratos?.list || [])
      setPropostas(resultPropostas?.list || [])
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    }
  }

  const contratosComPrazo = contratos.filter(c => c.prazo)
  const hoje = new Date()
  
  const urgentes = contratosComPrazo.filter(c => {
    const prazo = new Date(c.prazo)
    const diffDias = Math.ceil((prazo.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24))
    return diffDias <= 3 && diffDias >= 0
  })

  const estaSemana = contratosComPrazo.filter(c => {
    const prazo = new Date(c.prazo)
    const diffDias = Math.ceil((prazo.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24))
    return diffDias > 3 && diffDias <= 7
  })

  const esteMes = contratosComPrazo.filter(c => {
    const prazo = new Date(c.prazo)
    const diffDias = Math.ceil((prazo.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24))
    return diffDias > 7 && diffDias <= 30
  })

  const atrasados = contratosComPrazo.filter(c => {
    const prazo = new Date(c.prazo)
    return prazo < hoje
  })

  const gerarAnalise = async () => {
    setLoading(true)
    try {
      const response = await fetch('https://p410083387467890688.lumi.page/calendario-entregas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contratos,
          propostas,
          urgentes: urgentes.length,
          estaSemana: estaSemana.length,
          esteMes: esteMes.length,
          atrasados: atrasados.length
        })
      })

      if (!response.ok) throw new Error('Erro na requisição')
      
      const data = await response.json()
      setRelatorio(data.content)
    } catch (error) {
      console.error('Erro ao gerar análise:', error)
      alert('Erro ao gerar análise. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="p-3 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#8B0000]">Calendário Estratégico de Entregas</h1>
              <p className="text-sm text-gray-600">Organize e priorize suas entregas</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Cards de Status */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={20} className="text-red-600" />
              <span className="text-xs font-semibold text-gray-600">URGENTES</span>
            </div>
            <div className="text-2xl font-bold text-red-600">{urgentes.length}</div>
            <div className="text-xs text-gray-600">Até 3 dias</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-orange-200">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={20} className="text-orange-600" />
              <span className="text-xs font-semibold text-gray-600">ESTA SEMANA</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">{estaSemana.length}</div>
            <div className="text-xs text-gray-600">4-7 dias</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={20} className="text-blue-600" />
              <span className="text-xs font-semibold text-gray-600">ESTE MÊS</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{esteMes.length}</div>
            <div className="text-xs text-gray-600">8-30 dias</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={20} className="text-[#8B0000]" />
              <span className="text-xs font-semibold text-gray-600">ATRASADOS</span>
            </div>
            <div className="text-2xl font-bold text-[#8B0000]">{atrasados.length}</div>
            <div className="text-xs text-gray-600">Requerem atenção</div>
          </div>
        </div>

        {/* Listagem de Entregas Urgentes */}
        {urgentes.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-red-200 mb-6">
            <h2 className="text-lg font-bold text-red-600 mb-4 flex items-center gap-2">
              <AlertTriangle size={22} />
              Entregas Urgentes (Até 3 Dias)
            </h2>
            <div className="space-y-3">
              {urgentes.map(contrato => {
                const prazo = new Date(contrato.prazo)
                const diffDias = Math.ceil((prazo.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24))
                return (
                  <div key={contrato._id} className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-bold text-gray-900">{contrato.servicoOferecido}</h3>
                        <p className="text-xs text-gray-600 mt-1">Cliente: {contrato.nomeCliente}</p>
                        <p className="text-xs text-red-600 font-semibold mt-1">
                          Prazo: {prazo.toLocaleDateString('pt-BR')} ({diffDias === 0 ? 'HOJE' : `${diffDias} dia${diffDias > 1 ? 's' : ''}`})
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Botão Gerar Análise */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Calendar size={24} className="text-[#8B0000]" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Análise Estratégica com IA</h2>
              <p className="text-sm text-gray-600">Receba recomendações para organizar suas entregas</p>
            </div>
          </div>
          
          <button
            onClick={gerarAnalise}
            disabled={loading || contratos.length === 0}
            className="w-full bg-[#8B0000] hover:bg-[#6B0000] text-white px-6 py-4 rounded-lg font-semibold transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader size={20} className="animate-spin" />
                Gerando calendário estratégico...
              </>
            ) : (
              <>
                <Calendar size={20} />
                Gerar Plano de Entregas
              </>
            )}
          </button>

          {contratos.length === 0 && (
            <p className="text-sm text-gray-500 text-center mt-3">
              Crie contratos para gerar seu calendário de entregas
            </p>
          )}
        </div>

        {/* Relatório */}
        {relatorio && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar size={22} className="text-[#8B0000]" />
              Plano Estratégico de Entregas
            </h2>
            <div className="prose prose-sm max-w-none">
              <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {relatorio}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
